<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'

import GroovizAmount from '~/components/ui/GroovizAmount.vue'

import { useGrooverPlusPromo } from '~/composables/useGrooverPlusPromo'
import { useNavigateToPreviousPage } from '~/composables/useNavigateToPreviousPage'

import { useCartPromoStore } from '~/stores/cartPromo'
import { useDraftStore } from '~/stores/draft'
import { useMiscDraftProgressivePromosStore } from '~/stores/miscDraftProgressivePromos'
import { useMiscResizeStore } from '~/stores/miscResize'

import { Breakpoints } from '~/enums/breakpoints'

type Props = {
  influencersCount: number
  campaignCost: number
  displayedCost: number
  hideGrooviz?: boolean
  selectedWording?: boolean
}

const {
  influencersCount,
  displayedCost,
  campaignCost,
  hideGrooviz = false,
  selectedWording = false,
} = defineProps<Props>()

const { t } = useI18n()
const route = useRoute()

const { navigateToPreviousPage } = useNavigateToPreviousPage()
const { hasValidAmountForPromo } = useGrooverPlusPromo()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { ranks: RANKED_INFLUENCERS } = storeToRefs(
  useInfluencersRecommendationsStore(),
)
const { IS_GROOVER_PLUS_PROMO_APPLIED } = storeToRefs(useCartPromoStore())
const { PROGRESSIVE_PROMO_FROM_INF_COUNT } =
  useMiscDraftProgressivePromosStore()
const { $state: DRAFT } = useDraftStore()
const { $state: CART_PROMO } = useCartPromoStore()

const text = computed(() => {
  if (!selectedWording) {
    return t('components.campaignFooter.cartCounter.preselectedPros', {
      count: influencersCount,
    })
  }
  const count = influencersCount
  return SCREEN_WIDTH.value >= Breakpoints.sm600
    ? t(`components.campaignFooter.cartCounter.selectedProsDesktop`, { count })
    : t(`components.campaignFooter.cartCounter.selectedProsMobile`, { count })
})

const eligibleProgressivePromo = computed(() => {
  return PROGRESSIVE_PROMO_FROM_INF_COUNT(DRAFT.influencers_count)
})

const promoText = computed(() => {
  const promoToDisplay =
    !CART_PROMO.id && eligibleProgressivePromo?.value
      ? eligibleProgressivePromo?.value
      : CART_PROMO
  const promoValue = promoToDisplay.actions.campaign.value ?? 0
  if (promoValue === 0) return ''

  const isPercentage = promoToDisplay.actions.campaign.is_percentage ?? false
  return `-${promoValue}${isPercentage ? '%' : ''}`
})

const textClass = 'tw-text-body-sm 600:tw-text-body tw-font-medium'
const isCartDisplayed = computed(
  () => route.name?.toString().startsWith('draft-influencers__') ?? false,
)

const hasDiscount = computed(() =>
  Boolean(
    DRAFT.id &&
      hasValidAmountForPromo.value &&
      promoText.value !== '' &&
      (CART_PROMO.id || eligibleProgressivePromo.value?.id),
  ),
)

function handleHideCart(): void {
  const { draftId } = route.query
  const hash = RANKED_INFLUENCERS.value.length > 0 ? '#loaded' : ''
  navigateToPreviousPage(
    draftId ? `/draft/${draftId}/influencers/${hash}` : '/',
  )
}
</script>
<template>
  <div class="tw-flex">
    <NuxtLinkLocale
      v-if="DRAFT.id && !isCartDisplayed && !!DRAFT.influencers_count"
      :to="`/draft/influencers/?draftId=${DRAFT.id}`"
    >
      <span
        :class="textClass"
        class="tw-text-primary hover:tw-underline"
        data-test-id="influencerSelectedCounter"
      >
        {{ text }}
      </span>
    </NuxtLinkLocale>
    <span
      v-else-if="isCartDisplayed"
      :class="textClass"
      class="tw-pointer tw-cursor-pointer tw-text-primary lg:hover:tw-underline"
      @click="handleHideCart"
    >
      {{ t('components.campaignFooter.toggleCart.hide') }}
    </span>
    <span v-else :class="textClass" class="tw-text-black">
      {{ text }}
    </span>
    <div v-if="!hideGrooviz" class="tw-mx-1 tw-flex tw-gap-1">
      <span class="tw-font-bold">•</span>
      <span :class="textClass" class="tw-text-black">
        <GroovizAmount :amount="displayedCost" />
      </span>
      <transition name="fade">
        <div
          v-if="hasDiscount"
          class="tw-flex tw-items-center tw-justify-start tw-gap-1"
        >
          <span :class="textClass" class="tw-text-discrete2">
            <GroovizAmount :amount="campaignCost" strikethrough />
          </span>
          <span class="tw-flex tw-items-center tw-gap-3xs">
            <span
              :class="[
                textClass,
                IS_GROOVER_PLUS_PROMO_APPLIED
                  ? 'tw-text-accent'
                  : 'tw-text-success',
              ]"
            >
              {{ promoText }}
            </span>
            <i
              v-if="!CART_PROMO.actions.campaign.is_percentage"
              class="icon-grooviz tw-text-base tw-font-medium tw-text-orange-500"
            />
            <UIIcon
              v-if="IS_GROOVER_PLUS_PROMO_APPLIED"
              name="groover:groover-plus"
              class="tw-text-accent"
            />
          </span>
        </div>
      </transition>
    </div>
  </div>
</template>
